/**
 * /cheap-broadband — SEO landing page
 * Target: "cheap broadband", "cheap broadband UK", "affordable broadband", "cheap internet"
 * Beats: Three, Vodafone, Sky — on dedicated mobile broadband landing pages
 */
import { Link } from 'react-router-dom'
import { CheckCircle, ArrowRight, Wifi, Zap, MapPin, Shield, Globe } from 'lucide-react'
import SEOHead from '@/components/SEOHead'
import { rollingFaqs } from '@/data/rollingPlans'
import SiluxPromise from '@/components/SiluxPromise'

const FAQS = [
  ...rollingFaqs().slice(0, 3),
  {
    question: 'What is the cheapest broadband in the UK?',
    answer: 'Silux Mobile mobile broadband starts at £15.95/month on a 24-month plan — either Broadband Lite on Three or our portable MiFi. No landline is required and the router is included and delivered to you. Prefer a shorter commitment? Plans start from 1 month — rolling monthly Broadband Lite is £25.95/month with 30 days’ notice to leave — and 3-month plans are from £19.95/month plus a £15 refundable holding deposit.',
  },
  {
    question: 'Do I need a phone line for cheap broadband?',
    answer: 'No! With Silux Mobile mobile broadband, you use a MiFi router or a 4G/5G router with a SIM — no phone line, no engineer visit, nothing drilled into your wall. Your router arrives by post; plug it in and connect.',
  },
  {
    question: 'How fast is cheap mobile broadband?',
    answer: 'Our 5G mobile broadband delivers speeds of 80–400 Mbps. On 4G, typical speeds are 10–50 Mbps with Broadband Lite and 50–150 Mbps with 5G Premium. This is fast enough for HD streaming, video calls and working from home.',
  },
  {
    question: 'Is mobile broadband reliable for rural areas?',
    answer: 'Yes — mobile broadband is often the best option for rural areas where fibre isn\'t available. Our Three network covers 99% of the UK population outdoors, including rural areas where fixed-line broadband struggles.',
  },
  {
    question: 'How long is a Silux Mobile broadband plan?',
    answer: 'You choose, from 1 month: a rolling monthly contract from £25.95/month (1-month minimum term, 30 days’ notice to leave, £30 refundable holding deposit), or a fixed 3, 6, 12, 18, 24 or 36 months. Longer plans get the lowest monthly price — Broadband Lite is £15.95/month over 24 months — and 3-month plans start at £19.95/month plus a £15 refundable holding deposit. Whichever term you pick, the price is fixed for the life of the plan and the broadband moves with you if you move home within the UK.',
  },
  {
    question: 'Will my broadband price increase during my contract?',
    answer: 'No. Your price is fixed for the life of your plan — there are no price increases during your contract and no annual inflation-linked price rises. What you sign up at is what you pay.',
  },
  {
    question: 'Are broadband upgrades free?',
    answer: 'Yes. Silux Mobile offers free broadband upgrades — move to a faster plan or upgrade your router with no upgrade fee. Free upgrades are included as standard.',
  },
  {
    question: 'Is Silux Mobile a good mobile broadband provider?',
    answer: 'Yes. Silux Mobile is a UK mobile broadband and fast internet provider on the Three, O2 and Vodafone networks — home broadband without a landline, from £15.95/month, with no price increases during your contract and free broadband upgrades.',
  },
]

const PLANS = [
  {
    name: 'MiFi Portable',
    price: 'From £15.95/mo',
    speed: 'Up to 50Mbps',
    data: 'Unlimited data',
    contract: '24-month plan',
    ideal: 'Browsing on the move',
    to: '/broadband/mifi',
  },
  {
    name: 'Broadband Lite',
    price: 'From £15.95/mo',
    speed: '10–50 Mbps',
    data: 'Unlimited data',
    contract: '24-month plan',
    ideal: 'HD streaming & WFH',
    to: '/broadband/lite',
    popular: true,
  },
  {
    name: '5G Premium',
    price: 'From £19.95/mo',
    speed: '80–400 Mbps',
    data: 'Unlimited data',
    contract: '36-month plan',
    ideal: '4K streaming & gaming',
    to: '/broadband/premium',
  },
  {
    name: 'Starlink Broadband',
    // Not on sale yet — the Starlink page takes registrations of interest and quotes no
    // price, so this card must not quote one either (checked 2026-09-22: no Starlink
    // product exists in the catalogue).
    price: 'Coming soon',
    speed: 'Satellite',
    data: 'Unlimited',
    contract: 'Register your interest',
    ideal: 'Remote & rural areas',
    to: '/broadband/starlink',
  },
]

export default function CheapBroadband() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'Cheap Broadband', url: 'https://www.siluxmobile.co.uk/cheap-broadband' },
  ]

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  }

  return (
    <>
      <SEOHead
        title="Cheap Broadband UK | Mobile Broadband From £15.95/Month | Silux Mobile"
        description="Get cheap broadband in the UK with Silux Mobile. No landline, no engineer, router included — 4G & 5G mobile broadband from £15.95/month. Plans from 1 month rolling to 36 months on Three, O2 and Vodafone. No price rises during your contract and free broadband upgrades."
        keywords={[
          'cheap broadband', 'cheap broadband UK', 'affordable broadband UK', 'cheap internet UK',
          'short term broadband', 'mobile broadband UK', '4G broadband', '5G broadband',
          'broadband without landline', 'rural broadband UK', 'cheap wifi UK', 'home broadband UK',
          'fast broadband provider', 'rural broadband provider', 'alternative broadband provider',
          'broadband provider near me', 'cheap internet provider UK', 'mobile broadband provider',
          'best mobile broadband provider UK', 'fast internet provider', '4G broadband provider',
          '5G broadband provider', 'home broadband provider', 'broadband provider without landline',
          'wireless broadband provider', 'internet provider UK',
        ]}
        canonical="https://www.siluxmobile.co.uk/cheap-broadband"
        breadcrumbs={breadcrumbs}
        faqs={FAQS}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('/grid.svg')]" />
        <div className="max-w-5xl mx-auto relative text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 text-sm font-medium">
            <Wifi className="w-4 h-4" />
            Cheap Broadband — No Landline Required
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Cheap Broadband UK
            <span className="block text-2xl md:text-3xl font-normal text-white/80 mt-2">
              4G & 5G from £15.95/month — no landline, no engineer
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Silux Mobile is a cheap internet provider UK households trust — a fast broadband provider and mobile broadband provider that makes broadband simple and affordable. No phone line, no engineer visit, no setup fee, and your router is included and delivered to your door.
            Choose the plan length that suits you, from a 1-month rolling contract to 36 months.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/broadband/guide"
              className="px-8 py-4 bg-white text-teal-700 rounded-2xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Find My Broadband Package
            </Link>
            <Link
              to="/broadband"
              className="px-8 py-4 bg-white/20 border-2 border-white/40 text-white rounded-2xl font-bold hover:bg-white/30 transition-all"
            >
              View All Plans
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-white/70">
            <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> 80–400 Mbps on 5G</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-400" /> Plans From 1 Month</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-300" /> 99% UK Coverage</span>
          </div>
        </div>
      </section>

      <SiluxPromise variant="broadband" />

      {/* Why Mobile Broadband */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Why Choose Mobile Broadband Over Fibre?
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Traditional fibre broadband needs a phone line, an engineer and a socket drilled into your wall. Mobile broadband arrives in the post, works the same day, moves house with you — and is often cheaper.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'No Engineer Visit',
                desc: 'Your SIM card arrives by post. Insert it into your MiFi device and you\'re online in minutes — no waiting weeks for installation.',
                icon: Zap,
              },
              {
                title: 'Works Anywhere',
                desc: 'Take your broadband wherever you go. Perfect for renters, rural properties, caravans, offices and temporary accommodation.',
                icon: Globe,
              },
              {
                title: 'Router Included',
                desc: 'Your 4G/5G router comes with the package and lands on your doormat ready to go. No line rental, no setup fees, no activation charges — the price you agree is the price you pay every month.',
                icon: Shield,
              },
            ].map(item => (
              <div key={item.title} className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                <item.icon className="w-8 h-8 text-teal-600 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Cheap Broadband Plans
          </h2>
          <p className="text-center text-gray-600 mb-10">
            On Three, O2 or Vodafone — router included and delivered. Check your coverage first.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PLANS.map(plan => (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-6 border-2 transition-all hover:shadow-xl hover:-translate-y-1 ${
                  plan.popular ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full bg-teal-600 text-white">
                    Best Value
                  </span>
                )}
                <h3 className="font-bold text-gray-900 mb-1">{plan.name}</h3>
                <p className={`text-2xl font-black mb-3 ${plan.popular ? 'text-teal-600' : 'text-gray-900'}`}>
                  {plan.price}
                </p>
                <ul className="space-y-2 mb-6 text-sm">
                  {[plan.speed, plan.data, plan.contract, `Ideal for: ${plan.ideal}`].map(f => (
                    <li key={f} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.to}
                  className={`block text-center py-3 rounded-xl font-semibold text-sm transition-all ${
                    plan.popular
                      ? 'bg-teal-600 text-white hover:bg-teal-700'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Get This Plan
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            Prices shown are our best monthly rate. Lite and 5G Premium are also available on a rolling monthly contract from £25.95/month (30 days’ notice), and every package over 3, 6, 12 and 18 months — 3-month plans start at £19.95/month plus a £15 refundable holding deposit.
          </p>
          <p className="text-center text-sm text-gray-500 mt-3">
            Not sure which plan? <Link to="/broadband/guide" className="text-teal-600 font-semibold hover:underline">Use our broadband guide</Link> to find the best fit for your postcode.
          </p>
        </div>
      </section>

      {/* GEO Coverage */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
            Cheap Broadband Available UK-Wide
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Our 4G and 5G mobile broadband covers 99% of the UK population — from city centres to rural villages.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              'London Broadband', 'Manchester Broadband', 'Birmingham Broadband', 'Leeds Broadband',
              'Glasgow Broadband', 'Edinburgh Broadband', 'Bristol Broadband', 'Cardiff Broadband',
              'Rural Broadband', 'No-Line Broadband', 'Village Broadband', 'Farm Broadband',
            ].map(area => (
              <div key={area} className="bg-gray-50 rounded-xl px-4 py-3 text-center text-sm font-medium text-gray-700 border border-gray-200">
                {area}
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link
              to="/network-coverage"
              className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:underline"
            >
              <MapPin className="w-4 h-4" />
              Check coverage in your postcode
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
            Cheap Broadband FAQs
          </h2>
          <div className="space-y-4">
            {FAQS.map(faq => (
              <details key={faq.question} className="group border border-gray-200 rounded-xl overflow-hidden bg-white">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-gray-900 hover:bg-gray-50">
                  {faq.question}
                  <ArrowRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-10 px-4 bg-teal-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">You Might Also Need</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Rolling Monthly Broadband', to: '/rolling-monthly-broadband' },
              { label: 'Mobile Broadband', to: '/mobile-broadband' },
              { label: 'Cheap SIM Cards', to: '/cheap-sim-cards' },
              { label: 'Data SIM Cards', to: '/data-sim-cards' },
              { label: 'Starlink Broadband', to: '/broadband/starlink' },
              { label: 'SIM Only Deals', to: '/sim-only' },
              { label: 'Network Coverage', to: '/network-coverage' },
              { label: 'Broadband Guide', to: '/broadband/guide' },
              { label: 'Contact Us', to: '/contact' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="block p-3 bg-white rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:border-teal-400 hover:text-teal-700 transition-all text-center"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
